import { Check } from "lucide-react";

const SPECS = [
  { k: "Ink coverage", v: "Full CMYK + opaque white underbase" },
  { k: "Wash test", v: "60+ industrial cycles, no cracking" },
  { k: "Garment range", v: "Cotton, poly-blend, fleece, denim, canvas" },
  { k: "Print zone", v: "Up to A3 / 30 × 40 cm" },
  { k: "Min order", v: "1 piece (no minimums)" },
  { k: "File formats", v: "PNG, AI, PSD, PDF (300dpi)" },
];

export default function Services() {
  return (
    <div data-testid="services-page" className="px-6 md:px-12 lg:px-20 py-20 md:py-28">
      {/* Header */}
      <div className="grid grid-cols-1 md:grid-cols-12 gap-8 mb-20">
        <div className="md:col-span-7">
          <p className="font-mono text-xs uppercase tracking-[0.3em] text-[#71717A] mb-6">
            What we do
          </p>
          <h1 className="font-display font-black text-6xl md:text-8xl leading-[0.85] tracking-tighter">
            Two presses<span className="text-[#E6007A]">.</span>
            <br />
            One studio<span className="text-[#00E5FF]">.</span>
          </h1>
        </div>
        <div className="md:col-span-5 self-end">
          <p className="font-mono text-base leading-relaxed text-[#27272A]">
            Xemi runs a full DTF press and a dye-sublimation press out of one
            small Cornwall studio. Whatever you need printed — a single tee,
            a capsule drop or a brand activation — send us the artwork and
            we'll come back the same day with a custom quote.
          </p>
        </div>
      </div>

      {/* Print methods */}
      <div className="mb-24">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 md:gap-6">
          {[
            {
              tag: "Method 01",
              accent: "#E6007A",
              name: "DTF — Direct-to-Film",
              desc: "Full-colour CMYK + opaque white underbase. Heat-pressed onto cotton, polyester, fleece, denim, and dark garments. Soft hand, no plasticky feel.",
              best: ["T-shirts & hoodies (any colour)", "Caps & hats", "Workwear & cotton blends", "Detailed photo-realistic art", "Small batch / single pieces"],
            },
            {
              tag: "Method 02",
              accent: "#00E5FF",
              name: "Sublimation",
              desc: "Dye is gas-bonded into white polyester fibres for a no-feel, edge-to-edge print. Wash-proof, fade-proof and ideal for all-over designs.",
              best: ["White / light poly tees & hoodies", "All-over prints & patterns", "Sportswear & jerseys", "Mugs, coasters & hard goods"],
            },
          ].map((m) => (
            <div
              key={m.name}
              data-testid={`method-${m.name.split(" ")[0].toLowerCase()}`}
              className="border border-black/15 bg-white p-8"
            >
              <div className="flex items-center gap-3 mb-6">
                <span className="w-3 h-3" style={{ backgroundColor: m.accent }} />
                <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#71717A]">
                  {m.tag}
                </p>
              </div>
              <h3 className="font-display font-black text-3xl md:text-4xl mb-4 leading-[0.95]">
                {m.name}
              </h3>
              <p className="font-mono text-sm text-[#52525B] leading-relaxed mb-6">
                {m.desc}
              </p>
              <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#71717A] mb-3">
                Best for
              </p>
              <ul className="space-y-2">
                {m.best.map((b) => (
                  <li key={b} className="flex items-start gap-3 font-mono text-sm">
                    <Check size={16} className="mt-0.5 shrink-0" />
                    <span>{b}</span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </div>

      {/* Tech specs */}
      <div className="border-t border-black/15 pt-20">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-8 mb-12">
          <div className="md:col-span-5">
            <p className="font-mono text-xs uppercase tracking-[0.3em] text-[#71717A] mb-6">
              Technical specs
            </p>
            <h2 className="font-display font-black text-5xl md:text-6xl leading-[0.9]">
              No fine print.
              <br />
              Just spec.
            </h2>
          </div>
          <div className="md:col-span-7">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-px bg-black/10 border border-black/10">
              {SPECS.map((s) => (
                <div key={s.k} className="bg-white p-6">
                  <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#71717A] mb-2">
                    {s.k}
                  </p>
                  <p className="font-display font-black text-lg leading-tight">{s.v}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
